import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Eye, Globe, FileText, X, Sparkles, Loader2, Check } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { MarkdownEditor } from "@/components/blog/MarkdownEditor";
import { ImageUpload } from "@/components/blog/ImageUpload";
import { SeoReviewDrawer } from "@/components/blog/SeoReviewDrawer";

const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const token = localStorage.getItem("fra_admin_token");
  const base = process.env.REACT_APP_BACKEND_URL;
  const resp = await fetch(`${base}/api/admin/blog/upload`, {
    method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
  const data = await resp.json();
  return data.path;
};

const StatusPill = ({ status }) => (
  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
    status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
    {status}
  </span>
);

function PostsList({ posts, cats, filter, onFilter, onNew, onEdit, onDuplicate, onDelete, onPublish, onUnpublish }) {
  return (
    <div className="space-y-3" data-testid="blog-posts-list">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={onNew} data-testid="blog-new-post"
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3.5 py-2 text-sm font-semibold text-white">
          <Plus className="h-4 w-4" /> New post
        </button>
        <select data-testid="blog-status-filter" value={filter.status} onChange={(e) => onFilter({ ...filter, status: e.target.value })}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs">
          <option value="all">All statuses</option>
          <option value="published">Published only</option>
          <option value="draft">Drafts only</option>
        </select>
        <select data-testid="blog-cat-filter" value={filter.cat} onChange={(e) => onFilter({ ...filter, cat: e.target.value })}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs">
          <option value="all">All categories</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left">
              {["Title", "Author", "Category", "Status", "Updated", ""].map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`blog-row-${p.slug}`}>
                <td className="px-3 py-2 font-medium text-slate-900">
                  <button className="text-left hover:underline" onClick={() => onEdit(p)} data-testid={`blog-edit-${p.slug}`}>
                    {p.title}
                  </button>
                  <p className="text-[11px] text-slate-400">/blog/{p.slug}</p>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{p.author?.name || "-"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{p.category?.name || "-"}</td>
                <td className="px-3 py-2"><StatusPill status={p.status} /></td>
                <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(p.updated_at)}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    {p.status === "published" ? (
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" aria-label="View live" data-testid={`blog-view-${p.slug}`}
                         className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Globe className="h-4 w-4" /></a>
                    ) : (
                      <a href={`/blog/preview/${p.preview_token}`} target="_blank" rel="noreferrer" aria-label="Preview" data-testid={`blog-preview-${p.slug}`}
                         className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Eye className="h-4 w-4" /></a>
                    )}
                    <button onClick={() => onDuplicate(p)} aria-label="Duplicate" data-testid={`blog-dup-${p.slug}`}
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Copy className="h-4 w-4" /></button>
                    {p.status === "published" ? (
                      <button onClick={() => onUnpublish(p)} data-testid={`blog-unpublish-${p.slug}`}
                              className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">Unpublish</button>
                    ) : (
                      <button onClick={() => onPublish(p)} data-testid={`blog-publish-${p.slug}`}
                              className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700">Publish</button>
                    )}
                    <button onClick={() => onDelete(p)} aria-label="Delete" data-testid={`blog-delete-${p.slug}`}
                            className="rounded p-1 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {posts.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-500">No posts yet. Click <strong>New post</strong> to start.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PostEditor({ post, cats, authors, onBack, onSaved, onPublish, onSeo }) {
  const [draft, setDraft] = useState(post);
  const [busy, setBusy] = useState(false);
  const [backlinks, setBacklinks] = useState([]);
  const [showBacklinks, setShowBacklinks] = useState(false);

  // Sync whenever the server-side post reference changes (not just id), so SEO-drawer
  // saves and publish responses refresh the editor instead of being overwritten.
  useEffect(() => { setDraft(post); }, [post]);
  useEffect(() => {
    api.get("/admin/blog/backlinks", true).then(setBacklinks).catch(() => {});
  }, []);

  const update = (patch) => setDraft((d) => ({
    ...d, ...patch,
    // Auto-derive slug from the title while the post is still a draft, until the user
    // (or the SEO drawer) has set a slug of their own.
    ...(patch.title !== undefined && d.status === "draft" && (!d.slug || d.slug.startsWith("untitled-draft"))
      ? { slug: patch.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80) || d.slug }
      : {}),
  }));

  const save = async () => {
    setBusy(true);
    try {
      const saved = await api.put(`/admin/blog/posts/${post.id}`, {
        title: draft.title, slug: draft.slug, excerpt: draft.excerpt, body: draft.body,
        hero_image: draft.hero_image, hero_alt: draft.hero_alt,
        category_id: draft.category_id, author_id: draft.author_id,
        meta_title: draft.meta_title, meta_description: draft.meta_description,
        og_title: draft.og_title, og_description: draft.og_description,
        twitter_title: draft.twitter_title, twitter_description: draft.twitter_description,
        tldr: draft.tldr, faq: draft.faq, backlinks: draft.backlinks,
        keywords: draft.keywords, social_post: draft.social_post,
      }, true);      setDraft(saved);
      onSaved(saved);
      toast.success("Draft saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const toggleBacklink = (url) => update({
    backlinks: (draft.backlinks || []).includes(url)
      ? draft.backlinks.filter((u) => u !== url)
      : [...(draft.backlinks || []), url],
  });

  return (
    <div className="space-y-4" data-testid="blog-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={onBack} data-testid="blog-editor-back"
                className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900">
          <X className="h-4 w-4" /> Back to posts
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={draft.status} />
          <button onClick={save} disabled={busy} data-testid="blog-save-draft"
                  className="rounded-md border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-800 disabled:opacity-50">
            {busy ? "Saving..." : "Save draft"}
          </button>
          <button onClick={async () => {
                    setBusy(true);
                    try {
                      await api.put(`/admin/blog/posts/${post.id}`, {
                        title: draft.title, slug: draft.slug, body: draft.body,
                        hero_image: draft.hero_image, hero_alt: draft.hero_alt,
                        category_id: draft.category_id, author_id: draft.author_id,
                      }, true);
                      const r = await api.post(`/admin/blog/posts/${post.id}/ai-fill`, {}, true);
                      setDraft(r.post);
                      onSaved(r.post);
                      const n = Object.keys(r.applied || {}).length;
                      toast.success(`AI filled ${n} field${n === 1 ? "" : "s"}`);
                    } catch (e) { toast.error(errMsg(e)); }
                    finally { setBusy(false); }
                  }} disabled={busy} data-testid="blog-ai-autofill"
                  className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-red-700 to-red-800 px-3.5 py-2 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50">
            <Sparkles className="h-4 w-4" /> AI auto-fill all
          </button>
          <button onClick={onSeo} data-testid="blog-open-seo"
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3.5 py-2 text-sm font-semibold text-white">
            <Sparkles className="h-4 w-4" /> Fine-tune
          </button>
          <button onClick={async () => { await save(); onPublish(draft); }} data-testid="blog-publish-btn"
                  className="rounded-md bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            {draft.status === "published" ? "Save & keep live" : "Publish"}
          </button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <input data-testid="blog-title" value={draft.title || ""} onChange={(e) => update({ title: e.target.value })}
                 placeholder="Post title"
                 className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 font-display text-2xl font-bold tracking-tight focus:outline-none focus:ring-2 focus:ring-slate-900" />
          <MarkdownEditor value={draft.body} onChange={(v) => update({ body: v })} onImageUpload={uploadImage} />
        </div>

        <aside className="space-y-4">
          <div className="rounded-md border border-slate-200 bg-white p-4">
            <ImageUpload value={draft.hero_image} alt={draft.hero_alt}
                         onChange={(path) => update({ hero_image: path })}
                         label="Hero image" testid="blog-hero-upload" />
            <div className="mt-3">
              <label className="label-caps">Hero alt text</label>
              <input data-testid="blog-hero-alt" value={draft.hero_alt || ""} onChange={(e) => update({ hero_alt: e.target.value })}
                     className="mt-1.5 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
            </div>
          </div>

          <div className="space-y-3 rounded-md border border-slate-200 bg-white p-4">
            <div>
              <label className="label-caps">Category</label>
              <select data-testid="blog-category-select" value={draft.category_id || ""} onChange={(e) => update({ category_id: e.target.value || null })}
                      className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm">
                <option value="">Choose a category</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps">Author</label>
              <select data-testid="blog-author-select" value={draft.author_id || ""} onChange={(e) => update({ author_id: e.target.value || null })}
                      className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm">
                <option value="">Choose an author</option>
                {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps">URL slug</label>
              <input data-testid="blog-slug" value={draft.slug || ""} onChange={(e) => update({ slug: e.target.value })}
                     className="mt-1.5 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-mono" />
            </div>
            <div>
              <label className="label-caps">Short excerpt</label>
              <textarea data-testid="blog-excerpt" value={draft.excerpt || ""} onChange={(e) => update({ excerpt: e.target.value })} rows={3}
                        placeholder="Shown on the index and in link previews. Used as the meta description if you leave the SEO field blank."
                        className="mt-1.5 w-full rounded-md border border-slate-300 p-2.5 text-sm" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="label-caps">Meta description</label>
                <span className={`text-[10px] font-medium ${(draft.meta_description || "").length > 155 ? "text-red-700" : "text-slate-400"}`}>
                  {(draft.meta_description || "").length}/155
                </span>
              </div>
              <textarea data-testid="blog-meta-description" value={draft.meta_description || ""} onChange={(e) => update({ meta_description: e.target.value })} rows={2}
                        placeholder="How Google shows your post in search results. Leave blank to reuse the excerpt."
                        className="mt-1.5 w-full rounded-md border border-slate-300 p-2.5 text-sm" />
              <p className="mt-1 text-[11px] text-slate-500">
                Required for publish. If left blank, we'll copy the excerpt. Use the SEO drawer for AI suggestions.
              </p>
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-4">
            <button onClick={() => setShowBacklinks((s) => !s)} data-testid="blog-backlinks-toggle"
                    className="flex w-full items-center justify-between">
              <span className="label-caps">Internal links ({(draft.backlinks || []).length})</span>
              <span className="text-xs text-slate-500">{showBacklinks ? "Hide" : "Browse"}</span>
            </button>
            {(draft.backlinks || []).length > 0 && (
              <ul className="mt-2 space-y-1 text-xs" data-testid="blog-backlinks-active">
                {(draft.backlinks || []).map((u) => (
                  <li key={u} className="flex items-center justify-between gap-2 rounded border border-slate-100 bg-slate-50 px-2 py-1">
                    <span className="truncate font-mono">{u}</span>
                    <button onClick={() => toggleBacklink(u)} aria-label="Remove" className="text-red-700">
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {showBacklinks && (
              <ul className="mt-3 max-h-56 space-y-1 overflow-auto text-xs">
                {backlinks.map((b) => {
                  const on = (draft.backlinks || []).includes(b.url);
                  return (
                    <li key={b.url}>
                      <button onClick={() => toggleBacklink(b.url)}
                              className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left ${on ? "bg-emerald-50 text-emerald-700" : "hover:bg-slate-50"}`}>
                        <span><span className="font-mono text-[10px] text-slate-400">{b.type}</span> {b.title}</span>
                        {on && <Check className="h-3 w-3" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Categories({ cats, onChange }) {
  const [draft, setDraft] = useState({ name: "", description: "", color: "#b91c1c" });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/blog/categories/${editing}`, draft, true);
      else await api.post("/admin/blog/categories", draft, true);
      setDraft({ name: "", description: "", color: "#b91c1c" }); setEditing(null);
      toast.success(editing ? "Category updated" : "Category created");
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const edit = (c) => { setEditing(c.id); setDraft({ name: c.name, description: c.description, color: c.color }); };
  const del = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.del(`/admin/blog/categories/${c.id}`, true); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_380px]" data-testid="blog-categories">
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 bg-slate-50 text-left">{["Name", "Slug", "Posts", ""].map((h) => <th key={h} className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}</tr></thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.id} className="border-b border-slate-100">
                <td className="px-3 py-2"><span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ backgroundColor: c.color }} />{c.name}</span></td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{c.slug}</td>
                <td className="px-3 py-2 text-xs">{c.post_count}</td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => edit(c)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(c)} className="rounded p-1 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {cats.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-slate-500">No categories yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <aside className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
        <p className="label-caps">{editing ? "Edit category" : "New category"}</p>
        <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name" className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" data-testid="cat-name" />
        <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={2} placeholder="Short description" className="w-full rounded-md border border-slate-300 p-2.5 text-sm" data-testid="cat-desc" />
        <div className="flex items-center gap-2">
          <input type="color" value={draft.color} onChange={(e) => setDraft({ ...draft, color: e.target.value })} className="h-8 w-10 rounded border border-slate-300" data-testid="cat-color" />
          <span className="font-mono text-xs text-slate-500">{draft.color}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={!draft.name.trim()} data-testid="cat-save"
                  className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
            {editing ? "Save" : "Create"}
          </button>
          {editing && <button onClick={() => { setEditing(null); setDraft({ name: "", description: "", color: "#b91c1c" }); }} className="text-xs text-slate-500">Cancel</button>}
        </div>
      </aside>
    </div>
  );
}

function Authors({ authors, onChange }) {
  const [draft, setDraft] = useState({ name: "", bio: "", avatar_url: null, x_url: "", linkedin_url: "" });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/blog/authors/${editing}`, draft, true);
      else await api.post("/admin/blog/authors", draft, true);
      setDraft({ name: "", bio: "", avatar_url: null, x_url: "", linkedin_url: "" }); setEditing(null);
      toast.success(editing ? "Author updated" : "Author created");
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const edit = (a) => { setEditing(a.id); setDraft({ name: a.name, bio: a.bio, avatar_url: a.avatar_url, x_url: a.x_url || "", linkedin_url: a.linkedin_url || "" }); };
  const del = async (a) => {
    if (!window.confirm(`Delete author "${a.name}"?`)) return;
    try { await api.del(`/admin/blog/authors/${a.id}`, true); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_380px]" data-testid="blog-authors">
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 bg-slate-50 text-left">{["Name", "Slug", "Posts", ""].map((h) => <th key={h} className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}</tr></thead>
          <tbody>
            {authors.map((a) => (
              <tr key={a.id} className="border-b border-slate-100">
                <td className="px-3 py-2">{a.name}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{a.slug}</td>
                <td className="px-3 py-2 text-xs">{a.post_count}</td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => edit(a)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(a)} className="rounded p-1 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {authors.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-slate-500">No authors yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <aside className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
        <p className="label-caps">{editing ? "Edit author" : "New author"}</p>
        <ImageUpload value={draft.avatar_url} onChange={(path) => setDraft({ ...draft, avatar_url: path })} label="Headshot" testid="author-avatar" />
        <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Display name" className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" data-testid="author-name" />
        <textarea value={draft.bio} onChange={(e) => setDraft({ ...draft, bio: e.target.value })} rows={3} placeholder="Short bio" className="w-full rounded-md border border-slate-300 p-2.5 text-sm" data-testid="author-bio" />
        <input value={draft.x_url} onChange={(e) => setDraft({ ...draft, x_url: e.target.value })} placeholder="X / Twitter URL" className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs" data-testid="author-x" />
        <input value={draft.linkedin_url} onChange={(e) => setDraft({ ...draft, linkedin_url: e.target.value })} placeholder="LinkedIn URL" className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs" data-testid="author-linkedin" />
        <div className="flex gap-2">
          <button onClick={save} disabled={!draft.name.trim()} data-testid="author-save"
                  className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
            {editing ? "Save" : "Create"}
          </button>
          {editing && <button onClick={() => { setEditing(null); setDraft({ name: "", bio: "", avatar_url: null, x_url: "", linkedin_url: "" }); }} className="text-xs text-slate-500">Cancel</button>}
        </div>
      </aside>
    </div>
  );
}

export function AdminBlog() {
  const [sub, setSub] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [seoFor, setSeoFor] = useState(null);
  const [filter, setFilter] = useState({ status: "all", cat: "all" });

  const load = useCallback(async () => {
    try {
      const [p, c, a] = await Promise.all([
        api.get("/admin/blog/posts", true),
        api.get("/admin/blog/categories", true),
        api.get("/admin/blog/authors", true),
      ]);
      setPosts(p); setCats(c); setAuthors(a);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredPosts = useMemo(() => posts.filter((p) => {
    if (filter.status !== "all" && p.status !== filter.status) return false;
    if (filter.cat !== "all" && p.category_id !== filter.cat) return false;
    return true;
  }), [posts, filter]);

  const newPost = async () => {
    try {
      const p = await api.post("/admin/blog/posts", { title: "Untitled draft" }, true);
      await load();
      setEditingPost(p);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openEdit = async (p) => {
    try { const fresh = await api.get(`/admin/blog/posts/${p.id}`, true); setEditingPost(fresh); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const duplicate = async (p) => {
    try { await api.post(`/admin/blog/posts/${p.id}/duplicate`, {}, true); toast.success("Duplicated"); await load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete post "${p.title}"?`)) return;
    try { await api.del(`/admin/blog/posts/${p.id}`, true); await load(); if (editingPost?.id === p.id) setEditingPost(null); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const publish = async (p) => {
    try {
      const saved = await api.post(`/admin/blog/posts/${p.id}/publish`, {}, true);
      toast.success("Published");
      await load();
      setEditingPost(saved);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const unpublish = async (p) => {
    try { await api.post(`/admin/blog/posts/${p.id}/unpublish`, {}, true); toast.success("Reverted to draft"); await load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (editingPost) {
    return (
      <>
        <PostEditor post={editingPost} cats={cats} authors={authors}
                    onBack={() => { setEditingPost(null); load(); }}
                    onSaved={(p) => setEditingPost(p)}
                    onPublish={publish}
                    onSeo={async () => {
                      // Save current editor state first so the AI reviews what's on screen.
                      try {
                        const saved = await api.put(`/admin/blog/posts/${editingPost.id}`, editingPost, true);
                        setEditingPost(saved);
                      } catch { /* non-fatal; drawer still opens */ }
                      setSeoFor(editingPost.id);
                    }} />
        <SeoReviewDrawer postId={seoFor} open={!!seoFor}
                         onClose={() => setSeoFor(null)}
                         onSaved={async () => { const p = await api.get(`/admin/blog/posts/${editingPost.id}`, true); setEditingPost(p); load(); }} />
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-blog">
      <div className="flex gap-2">
        {["posts", "categories", "authors"].map((s) => (
          <button key={s} onClick={() => setSub(s)} data-testid={`blog-subtab-${s}`}
                  className={`rounded-md px-3.5 py-2 text-sm font-semibold capitalize ${sub === s ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
            {s}
          </button>
        ))}
      </div>
      {sub === "posts" && (
        <PostsList posts={filteredPosts} cats={cats} filter={filter} onFilter={setFilter}
                   onNew={newPost} onEdit={openEdit} onDuplicate={duplicate}
                   onDelete={del} onPublish={publish} onUnpublish={unpublish} />
      )}
      {sub === "categories" && <Categories cats={cats} onChange={load} />}
      {sub === "authors" && <Authors authors={authors} onChange={load} />}
    </div>
  );
}
